import { ReactNode, useEffect, useState } from 'react'

import { Box, Container, Flex, Show, useTheme } from '@chakra-ui/react'

import { useGetThemeQuery } from '@core/app/api/globalApi'
import { EmployeeHeader } from '@core/components/EmployeeHeader'
import { FeedbackModal } from '@core/components/FeedbackModal'
import { Footer } from '@core/components/Footer/Footer'
import { Header } from '@core/components/Header/Header'
import { MobileHeader } from '@core/components/mobileHeader/mobileHeader'
import { FeedbackModalProvider } from '@core/utils/contexts/FeedbackModalContext'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'
import useLocalStorageStore from '@core/utils/hooks/useLocalStorageStore'

import { Loader } from '@page/absence/components/Loader'

interface Props {
    children: ReactNode
    useContainer?: boolean
    useMobileHeader?: boolean
    showFooter?: boolean
    showHeader?: boolean
    bgColor?: string
    isProfilePage?: boolean
    fixedHeader?: boolean
    isEmployee?: boolean
    isEmployeeHeaderFixed?: boolean
}

const customCorporation = document.getElementById('__custom_route_data')?.getAttribute('data-corpId') ?? 0

export const Layout = ({
    children,
    bgColor,
    useContainer = true,
    useMobileHeader = true,
    showFooter = true,
    showHeader = true,
    isProfilePage = false,
    fixedHeader = false,
    isEmployee = false,
    isEmployeeHeaderFixed = false,
}: Props): JSX.Element => {
    const { appTheme, updateTheme, isExpired } = useAppTheme()
    const { data: themeData } = useGetThemeQuery({}, { skip: isExpired })
    const theme = useTheme()
    const [isFeedbackMenu, setIsFeedbackMenu] = useState(false)
    const [selectedFeedback, setSelectedFeedback] = useState<number | null>(null)

    const [storageData, setStorageData] = useLocalStorageStore(`theme_data_${customCorporation}`)

    let bg = appTheme?.newSettings?.general?.Background
        ? `#${appTheme?.newSettings?.general?.Background}`
        : theme.colors.bg[3]
    bg = storageData?.newSettings?.general?.Background ? `#${storageData?.newSettings?.general?.Background}` : bg

    useEffect(() => {
        if (themeData) {
            updateTheme(themeData)
            setStorageData(themeData)
        }
    }, [themeData])

    const getBackgroundColor = () => {
        // if (isLoading) {
        //     return 'transparent'
        // }

        if (bgColor) return bgColor
        return isEmployee ? '#EDEEF0' : bg
    }

    // console.log(!!bgColor, !!appTheme?.newSettings?.general?.Background, !!theme.colors.bg[3])

    if (isEmployee === null) return <Loader />

    return (
        <FeedbackModalProvider>
            <Flex minH={'100vh'} flexDirection={'column'} id={'app-container'}>
                {showHeader && !isEmployee && (
                    <Header
                        isProfilePage={isProfilePage}
                        fixedHeader={fixedHeader}
                        isFeedbackMenu={isFeedbackMenu}
                        setIsFeedbackMenu={setIsFeedbackMenu}
                        selectedFeedback={selectedFeedback}
                        setSelectedFeedback={setSelectedFeedback}
                    />
                )}
                {showHeader && isEmployee && <EmployeeHeader isFixed={isEmployeeHeaderFixed} />}
                <Box as={'main'} flex={1} h={'full'} backgroundColor={getBackgroundColor()}>
                    {useContainer ? (
                        <Container
                            maxW={{
                                base: isProfilePage ? 'container.md' : 'container.sm',
                                lg: isProfilePage ? 'container.lg' : 'container.sm',
                                xl: 'container.xl',
                            }}
                        >
                            {children}
                        </Container>
                    ) : (
                        children
                    )}
                </Box>
                {showFooter && <Footer />}
                <Show below={isProfilePage ? 'lg' : 'xl'}>
                    <MobileHeader
                        useMobileHeader={useMobileHeader}
                        isFeedbackMenu={isFeedbackMenu}
                        setIsFeedbackMenu={setIsFeedbackMenu}
                        selectedFeedback={selectedFeedback}
                        setSelectedFeedback={setSelectedFeedback}
                    />
                </Show>
            </Flex>
            <FeedbackModal />
        </FeedbackModalProvider>
    )
}
